/**
 * TRACI-1053 (CPOC-109): one line telling a tester that a handoff went to the Amazon Connect
 * rep desk, not LivePerson. Only added when the escalate reply says provider "connect" AND
 * testRoute (the backend's call: prod internal route, dev header, switch or test rep). A
 * share-dial shopper, like a LivePerson handoff, gets nothing: real shoppers never read
 * "test", "Connect" or "LivePerson" (Dylan, 2 Nov 2026).
 *
 * Client-only (never sent to the agent or the rep) and inside the waiting band, like the
 * slow-wait notice. It sits right under the "Connecting…" turn (after its enter-human
 * divider when there is one), so a rep message or a queued shopper message that landed
 * before the escalate reply does not push it out of place.
 */
import type { Message } from '../../types/chat.types';

export const CONNECT_DESK_NOTICE = 'Tester note: this chat went to the Connect desk, not LivePerson. Shoppers never see this line.';

export const connectDeskNoticeId = (connectingId: string | null | undefined) =>
  `connect-notice-${connectingId ?? 'none'}`;

/** `messages` with the notice added once; the same array when it is already there. */
export function withConnectDeskNotice(messages: Message[], connectingId: string | null | undefined): Message[] {
  const id = connectDeskNoticeId(connectingId);
  if (messages.some((m) => m.id === id)) return messages;
  const notice = {
    id,
    role: 'assistant',
    content: '',
    timestamp: new Date(),
    isClientOnly: true,   // never sent to the agent or the rep
    inHumanZone: true,    // inside the waiting band, like the slow-wait notice
    parts: [{ type: 'text', content: CONNECT_DESK_NOTICE }],
  } as Message;
  const at = connectingId ? messages.findIndex((m) => m.id === connectingId) : -1;
  if (at < 0) return [...messages, notice];
  let insertAt = at + 1;
  const next = messages[insertAt];
  if (next && next.responder === 'divider' && next.dividerKind === 'enter-human') insertAt += 1;
  return [...messages.slice(0, insertAt), notice, ...messages.slice(insertAt)];
}
